import { useCallback } from "react";
import styles from "../aic.module.css";
import { EditorDefns } from "../utility";
import { defaultEditorDefns } from "../utility/DefaultEditor";
import { AioReplacementList } from "./aioReplacementList";
import { AioExternalReplacements, AioReplacement, AioReplacementValues } from "./interface";

export interface AioReplacementValuesDisplayProps<T> {
  id: string;
  airid: string;
  texts: T[];
  spaceAfter?: boolean;
  subLists?: AioReplacement<T>[];
  setReplacementValue?: (ret: AioReplacementValues<T>) => void;
  dontAskSpace?: boolean;
  dontAskTrail?: boolean;
  externalLists?: AioExternalReplacements<T>[];
  editorProps?: EditorDefns<T>;
  level: number;
}

/**
 * Display and optionally edit a list of replacement values used by AIC.
 *
 * @param props - Destructured parameter object
 * @param props.id - Unique element id used for DOM attributes
 * @param props.airid - Identifier for the replacement value group
 * @param props.texts - Array of text values to display/edit
 * @param props.spaceAfter - When true, inserts a trailing space after replacement text
 * @param props.subLists - Nested replacement lists associated with this item
 * @param props.setReplacementValue - Callback invoked when the replacement values change
 * @param props.dontAskSpace - When true, the UI does not prompt for the "space after" option
 * @param props.dontAskTrail - When true, the UI does not prompt for trailing options
 * @param props.externalLists - External replacement lists available for selection
 * @param props.editorProps - Configuration object for the embedded editor component
 * @param props.level - Nesting level used to limit recursive rendering depth
 */
export const AioReplacementValuesDisplay = <T,>({
  id,
  airid,
  texts,
  spaceAfter = false,
  subLists = [],
  setReplacementValue,
  dontAskSpace,
  dontAskTrail,
  externalLists,
  editorProps = defaultEditorDefns as EditorDefns<T>,
  level,
}: AioReplacementValuesDisplayProps<T>): React.ReactElement => {
  const Editor = editorProps.Editor;
  const returnData = useCallback(
    (newRV: {
      externalName?: string;
      texts?: T[];
      spaceAfter?: boolean;
      subLists?: AioReplacement<T>[];
    }) => {
      /* istanbul ignore else */
      if (setReplacementValue) {
        const r: AioReplacementValues<T> = {
          airid: airid,
          spaceAfter: newRV.spaceAfter ?? spaceAfter,
          texts: newRV.texts ?? texts,
          subLists: newRV.subLists ?? subLists,
        };
        setReplacementValue(r);
      }
    },
    [airid, setReplacementValue, spaceAfter, subLists, texts],
  );

  return (
    <div className={styles.aiordvMain}>
      <div
        style={{
          marginRight: "4px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: "2px",
        }}
      >
        <Editor
          id={id}
          aria-label={"Replacement values"}
          editable={setReplacementValue !== undefined}
          value={editorProps.joinTintoBlock(texts)}
          setValue={(ret) => returnData({ texts: editorProps.splitTintoLines(ret) })}
          style={{
            width: "170px",
            minWidth: "170px",
            minHeight: "64px",
            marginTop: "4px",
            height: "Calc(100% - 16px)",
            padding: "4px",
            border: "1px solid black",
            borderRadius: "4px",
          }}
          resize
        />
        {!dontAskSpace && (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <label htmlFor={`${id}-spaceafter`}>
              <small>Space after repeat</small>
            </label>
            <input
              id={`${id}-spaceafter`}
              style={{ margin: "6px" }}
              type="checkbox"
              checked={spaceAfter}
              disabled={!setReplacementValue}
              onChange={(e) => returnData({ spaceAfter: e.currentTarget.checked })}
            />
          </div>
        )}
      </div>
      {setReplacementValue || subLists.length > 0 ? (
        <>
          {level < 4 && (
            <>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <div
                  style={{
                    flexGrow: 1,
                    minWidth: "5px",
                    width: "5px",
                    borderBottom: "1px burlywood solid",
                    borderBottomRightRadius: "4px",
                  }}
                />
                <div
                  style={{
                    flexGrow: 1,
                    minWidth: "5px",
                    width: "5px",
                    borderTop: "1px burlywood solid",
                    borderTopRightRadius: "4px",
                  }}
                />
              </div>
              <div
                style={{
                  minWidth: "5px",
                  width: "5px",
                  marginTop: "6px",
                  marginBottom: "6px",
                  borderLeft: "1px burlywood solid",
                  borderTop: "1px burlywood solid",
                  borderBottom: "1px burlywood solid",
                  borderTopLeftRadius: "4px",
                  borderBottomLeftRadius: "4px",
                }}
              />
            </>
          )}
          <AioReplacementList
            id={`${id}-sublists`}
            replacements={subLists}
            setReplacements={
              setReplacementValue ? (ret) => returnData({ subLists: ret }) : undefined
            }
            dontAskSpace={setReplacementValue ? dontAskSpace : true}
            dontAskTrail={dontAskTrail}
            externalLists={externalLists}
            editorProps={editorProps}
            level={level + 1}
          />
        </>
      ) : (
        <></>
      )}
    </div>
  );
};

AioReplacementValuesDisplay.displayName = "AioReplacementValuesDisplay";
